import { Note } from "../../../Data/HexMap/Note";
import { Music } from "../../../Util/Music/Music";
import { ChapterConfig } from "../../ChapterConfig";
import { OutlineBox } from "../../../Comps/OutlineBox/OutlineBox";
import { ChapterNav, ChapterNavItem } from "../ChapterNav/ChapterNav";
import { ChapterRoutes } from "../ChapterNav/ChapterRoutes";
import { ClearHelpers } from "../../Helpers/ClearHelpers/ClearHelpers";
import { HxMapGroup } from "../../../Comps/HxMap/HxMapGroup";
import { BuildHexMap} from "../../../Data/HexMap/HexMapBuilder";
import { HexMap } from "../../../Data/HexMap/HexMap";
import { TransformTranslate } from "../../../Util/Transform/Transform";
import { HexMapModifier } from "../../../Data/HexMap/HexMapModifier";
import { All } from "../../../Util/Constants/Quantities";
import { MutableRefObject, useRef, useState } from "react";
import { produce } from "immer";
import './KeyCompassChapter.scss'
import { Key } from "../../../Data/HexMap/Key";
import { Helpers } from "../../Helpers/Helpers";
import { FlexRowTwo } from "../../../Layouts/FlexRows";
import { HexCompassExercise } from "../../Exercises/HexCompass/HexCompassExercise";
import { NeighborNamer } from "../../Exercises/NeighborNamer/NeighborNamer";
export function KeyCompassChapter(props:ChapterConfig){
    const subPaths:ChapterNavItem[] = [
        {title:'Lesson', path:'lesson', 
            element:<ClearHelpers openHelpers={[Helpers.IntervalNames, Helpers.MusicalAlphabet, Helpers.HexPattern]}>
                <Lesson/></ClearHelpers>},
        {title:'Compass Exercise', isExercise:true, path:'compass_exercise', 
            element:<ClearHelpers openHelpers={[Helpers.KeyCompass]}>
                <HexCompassExercise/></ClearHelpers>},
        {title:'Neighbor Key Exercise', isExercise:true, path:'neighbor_key', 
            element:<ClearHelpers openHelpers={[Helpers.KeyCompass]}>
                <NeighborNamer/></ClearHelpers>}
    ]
    return  <div className={'key-compass-chapter'}>
                <h1>Relative Keys and The Key Compass</h1>
                <ChapterNav path={props.path} subPaths={subPaths}/>
                <ChapterRoutes chapterNavs={subPaths}/>
            </div>
}

export function Lesson(){
    const radius:number = 65
    const columns:number = 4
    const rows:number = 3
    const width:number = 400
    const containerWidth:number = 900
    const height:number = 300
    const tonic:number = Music.Gb
    const getHexMap = () => {  
        const hexMap:HexMap = BuildHexMap({columns:columns, rows:rows, tonic:tonic, radius:radius})
        const mod:HexMapModifier = new HexMapModifier(hexMap)
        const centerKey:number = 5
        mod.keys_setHilited([centerKey])
        mod.keys_setOpen([All], false)
        //mod.keys_setVisible([All], false)
        //mod.keys_setVisible(keyIndexes.concat([centerKey]), true)
        mod.keys_setPredefinedLabel([0],'7th')
        mod.keys_setPredefinedLabel([1],'b6th')
        mod.keys_setPredefinedLabel([2],'4th')
        mod.keys_setPredefinedLabel([3],'2nd')
        mod.keys_setPredefinedLabel([4],'b3rd')
        mod.keys_setPredefinedLabel([6],'6th')
        mod.keys_setPredefinedLabel([7],'b5th')
        mod.keys_setPredefinedLabel([8],'b7th')
        mod.keys_setPredefinedLabel([9],'5th')
        mod.keys_setPredefinedLabel([10],'3rd')
        mod.keys_setPredefinedLabel([11],'b2nd')

        mod.keys_setSecondaryLabel([0],'11')
        mod.keys_setSecondaryLabel([1],'8')
        mod.keys_setSecondaryLabel([2],'5')
        mod.keys_setSecondaryLabel([3],'2')
        mod.keys_setSecondaryLabel([4],'3')
        mod.keys_setSecondaryLabel([6],'9')
        mod.keys_setSecondaryLabel([7],'6')
        mod.keys_setSecondaryLabel([8],'10')
        mod.keys_setSecondaryLabel([9],'7')
        mod.keys_setSecondaryLabel([10],'4')
        mod.keys_setSecondaryLabel([11],'1')
        mod.keys_setDefined([All], false)
        return hexMap
    }
    const getReferenceMap = () => {  
        const hexMap:HexMap = BuildHexMap({columns:columns, rows:rows, tonic:tonic, radius:radius})
        const mod:HexMapModifier = new HexMapModifier(hexMap)
        const centerKey:number = 5
        mod.keys_setHilited([centerKey])
        mod.keys_setOpen([All], false)
        mod.keys_setOpen([centerKey], true)
        mod.notes_setHalo([All], true)
        //mod.keys_setVisible([All], false)
        //mod.keys_setVisible(keyIndexes.concat([centerKey]), true)
        return hexMap
    } 
    const [hexMap, setHexMap] = useState(getHexMap())
    const [refMap, setRefMap] = useState(getReferenceMap())
    const scale:number = 1
    const mod:MutableRefObject<HexMapModifier> = useRef(new HexMapModifier(hexMap))
    const onKeyClick = (key:Key, e:React.MouseEvent) => {
        const keyIndex:number = key.params.keyIndex
        setHexMap(produce((draft:HexMap)=>{
            mod.current.setDraft(draft)
            mod.current.keys_setDefined([keyIndex], true)
            mod.current.keys_setSelected([keyIndex])
        }))
    } 
    const onRefKeyClick = (key:Key, e:React.MouseEvent) => {
        const keyIndex:number = key.params.keyIndex
        const selected:boolean = (key.selectedState === Note.SelectedState_Selected)
        setRefMap(produce((draft:HexMap)=>{
            mod.current.setDraft(draft)
            mod.current.keys_setDefined([keyIndex], true)
            if(e.metaKey){
                mod.current.keys_setOpen([keyIndex], !key.open)
                return
            }
            if(selected){
                mod.current.keys_setDefault([keyIndex])
            }
            else{
                mod.current.keys_setSelected([keyIndex])
            }
 
        }))
    }  
    

    return(
        <OutlineBox title={'Directional Steps and Intervals'} width={containerWidth}>
            <div className={'compass-chapter'} style={{paddingTop:40, paddingBottom:40}}>
                <FlexRowTwo columnOneFlex={1.5} columnTwoFlex={2}>
                    <svg className={'hex-map-svg'} width={width} height={height}>
                        <g transform={TransformTranslate(width/2, height/2)}>
                            <g className={'scale'} style={{scale:scale}}>
                            <HxMapGroup onKeyClick={onRefKeyClick} 
                                        hexMap={refMap}/>
                            </g>
                        </g>
                    </svg>
                    <svg className={'hex-map-svg'} width={width} height={height}>
                        <g transform={TransformTranslate(width/2, height/2)}>
                            <g className={'scale'} style={{scale:scale}}>
                            <HxMapGroup onKeyClick={onKeyClick} hexMap={hexMap}/>
                            </g>
                        </g>
                    </svg>
                </FlexRowTwo>
            </div>
        </OutlineBox>
    )
}
